import { ApiError } from '../features/identity/types';
import { isSubscriptionRestrictionCode } from './subscriptionRestriction';

/**
 * Turns a caught error into copy safe to show a user — never the raw "Request to /api/... failed with 401"
 * text `createJsonClient` generates when a response carries no problem-details `detail`, and never a raw
 * exception message.
 *
 * Support reference: an unexpected server failure shows the backend's own `X-Operation-Id` (carried on
 * `ApiError.operationId`, EPN-24A) so support can find the matching server log line. No reference is ever
 * fabricated client-side — if the response had none (or there was no response at all), none is shown.
 * Nothing is logged to the browser console: raw API/error data can contain sensitive detail, and the
 * server already logs the failure with the same operation ID.
 */
/** Shared verbatim with LoginPage/App.tsx's own session-expired banner (see IdentityContext.ts's
 * `sessionExpired` flag) so the two surfaces never drift apart on wording. */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

/** EPN-34 Phase 2 replaced the old unpartitioned "auth" rate-limit policy's 503 with a 429 on every
 * anonymous auth surface (login, refresh, password recovery, unlock, MFA send/verify) — see
 * `RateLimitPartitions.cs` on the backend. Generic on purpose: never reveals which specific limit
 * (per-IP vs. per-challenge resend cap, etc.) was hit. */
const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Please wait a few minutes and try again.';

export function friendlyErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return SESSION_EXPIRED_MESSAGE;
    // EPN-105: a subscription-state refusal is not a role problem — `err.message` already carries the safe
    // plain-language explanation (see createJsonClient), and the shell shows the next step.
    if (err.status === 403 && isSubscriptionRestrictionCode(err.code)) return err.message;
    if (err.status === 403) return "You don't have permission to do this.";
    if (err.status === 409) return 'This action conflicts with an existing record — it may already have been done.';
    if (err.status === 429) return TOO_MANY_ATTEMPTS_MESSAGE;
    // 503 is only ever thrown by this codebase's own ValidationExceptionHandler for a downstream-service
    // failure, always paired with a hand-authored, caller-safe detail (never a raw exception/stack trace) —
    // see e.g. PaymentProcessing.Api's PayScheduleClientUnavailableException mapping. Shown directly only
    // when the response really carried that detail; a 503 without one (e.g. a gateway/proxy page) gets the
    // generic message below instead of the frontend's technical fallback text.
    if (err.status === 503 && err.messageIsServerAuthored) return err.message;
    if (err.status >= 500) {
      return err.operationId
        ? `Something went wrong on our end. (Reference: ${err.operationId})`
        : 'Something went wrong on our end. Please try again, and contact support if it continues.';
    }
    // Other 4xx: structured validation/business detail authored by the server is intentionally actionable.
    // A frontend-generated technical fallback ("Request to /api/... failed with 400") is not, so the
    // caller's own fallback copy is used instead.
    return err.messageIsServerAuthored ? err.message : fallback;
  }

  return fallback;
}
